<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DatePicker from 'primevue/datepicker'
import { toMonthKey, monthKeyToDate } from '@/utils/date/date-time'

// Seletor de mês/ano (mês de referência da fatura), padronizado com o DatePicker
// PrimeVue usado no resto do app (locale pt-BR global → meses em português).
// Mantém a API em chave 'YYYY-MM' (formato que o backend espera). Por padrão não
// permite meses futuros (a fatura é sempre de um mês já fechado).
const props = withDefaults(
  defineProps<{
    modelValue: string // 'YYYY-MM'
    maxMonth?: string // 'YYYY-MM' (default: mês atual)
    placeholder?: string
  }>(),
  { maxMonth: undefined, placeholder: 'mm/aaaa' },
)

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>()

const toDate = (key?: string): Date | null => (key ? monthKeyToDate(key) : null)

const selectedDate = ref<Date | null>(toDate(props.modelValue))

const maxDate = computed(() => monthKeyToDate(props.maxMonth ?? toMonthKey(new Date())))

// Espelha mudanças externas sem criar loop com o watch de saída.
watch(
  () => props.modelValue,
  (value) => {
    const current = selectedDate.value ? toMonthKey(selectedDate.value) : ''
    if (value !== current) selectedDate.value = toDate(value)
  },
)

watch(selectedDate, (date) => {
  const key = date ? toMonthKey(date) : ''
  if (key !== props.modelValue) emit('update:modelValue', key)
})
</script>

<template>
  <DatePicker
    id="month-year-picker"
    v-model="selectedDate"
    view="month"
    dateFormat="mm/yy"
    :maxDate="maxDate"
    :manualInput="false"
    :placeholder="placeholder"
    showIcon
    iconDisplay="input"
    appendTo="body"
    fluid />
</template>

<style>
#month-year-picker {
  width: 100%;
  --p-inputtext-border-radius: 0;
  --p-datepicker-panel-border-radius: 0;
  --p-inputtext-focus-border-color: #123add;
}

#month-year-picker .p-inputtext,
#month-year-picker input {
  height: 2.75rem;
  width: 100%;
  border-radius: 0;
  border-width: 2px;
  border-color: #e2e8f0;
}

#month-year-picker .p-inputtext:hover,
#month-year-picker input:hover {
  border-color: #123add;
}

#month-year-picker .p-inputtext:enabled:focus,
#month-year-picker input:focus {
  border-color: #123add;
  box-shadow: none;
  outline: none;
}

#month-year-picker_panel {
  z-index: 70 !important;
  border-radius: 0;
  --p-datepicker-panel-border-radius: 0;
}

#month-year-picker_panel,
#month-year-picker_panel .p-datepicker,
#month-year-picker_panel .p-datepicker-header,
#month-year-picker_panel .p-datepicker-calendar-container,
#month-year-picker_panel button,
#month-year-picker_panel .p-datepicker-month,
#month-year-picker_panel .p-datepicker-year {
  border-radius: 0;
}
</style>
